import { useCallback } from 'react';
import { useApi } from 'api';
import { Customer, Invoice } from 'types';
import { logger } from '../utils/logger';

export type InvoiceWithCustomer = Invoice;

export interface UseCustomerFetchResult {
  /**
   * Fetches customer data by finding an invoice with that customer_id.
   * Uses the invoices list endpoint which includes embedded customer data.
   */
  fetchCustomerById: (customerId: number) => Promise<Customer | null>;
}

/**
 * Hook for fetching customer data.
 * 
 * Uses the invoices list endpoint to get customer data, since that endpoint
 * includes embedded customer objects while the single invoice endpoint does not.
 */
export function useCustomerFetch(): UseCustomerFetchResult {
  const api = useApi();

  const fetchCustomerById = useCallback(async (customerId: number): Promise<Customer | null> => {
    try {
      const filter = JSON.stringify([
        { field: 'customer_id', operator: 'eq', value: customerId }
      ]);
      
      const { data } = await api.getInvoices({ page: 1, per_page: 1, filter });
      
      if (data.invoices && data.invoices.length > 0) {
        return (data.invoices[0] as InvoiceWithCustomer).customer || null;
      }
      return null;
    } catch (err) {
      logger.warn('Error fetching customer via invoices:', err);
      return null;
    }
  }, [api]);

  return { fetchCustomerById };
}

export default useCustomerFetch;
